import React, { FC, Suspense, useState } from 'react'
import { Button, Loading, TitleController } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { MessageCardTimeline, MessageCategories } from '@pandiafi/components'

import { MessageThreadViewDataLoader } from './MessageThreadViewDataLoader'
import { MessageFormContainer } from '../Messages/MessageForm'
import { ThreadStack } from '../Messages/ThreadStack'
import { getReadOnlyMessageCategoryOptions } from '../Messages/utils'

import { residentMessageThreadKey } from 'api/apiHooks/useAPIGetResidentMessageThread'
import { SidebarInfoSection, SidebarInfoSectionText, View } from 'components'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'
import { getThreadSubject } from 'utils/getThreadSubject'
import { NotFound } from 'views/NotFound'
import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'

export const MessageThread: FC = () => {
  const queryClient = useQueryClient()
  const { t, i18n } = useTranslation(['common', 'messages', 'titles'])
  const { messagesPath } = useContractPaths()
  const { currentContractId: contractUuid } = useCurrentContract()

  const navi = useNavigate()
  const location = useLocation()
  const {
    reply,
    title,
  }: { reply: boolean | undefined; title: string | undefined } =
    location.state || {}

  const [showForm, setShowForm] = useState(reply ?? false)
  const { messageThreadUuid } = useParams() as { messageThreadUuid?: string }
  const { messaging, extendedMessaging } = useModuleConfig()

  const [formState, setFormState] = useState<{
    messageThreadUuid?: string
    replySubject?: string
    focusSubject: boolean
  }>({
    focusSubject: reply ?? false,
    messageThreadUuid,
    replySubject: title,
  })

  const handleCancel = () => {
    setShowForm(false)
    setFormState({ focusSubject: false })

    if (reply) {
      navi(messagesPath)
    }
  }

  if (!messaging) {
    return <NotFound />
  }

  return messageThreadUuid ? (
    <Suspense
      fallback={
        <ThreadLayout title={null}>
          <View.Content>
            <div className="flex items-center justify-center">
              <Loading variant="details" />
            </div>
          </View.Content>
        </ThreadLayout>
      }
    >
      <MessageThreadViewDataLoader messageThreadUuid={messageThreadUuid}>
        {(message) => (
          <ThreadLayout title={getThreadSubject(message)}>
            <View.Content>
              <>
                {extendedMessaging && (
                  <>
                    {showForm ? (
                      <MessageFormContainer
                        key={formState.messageThreadUuid ?? 'new'}
                        threadId={formState.messageThreadUuid}
                        replySubject={formState.replySubject}
                        focusSubject={formState.focusSubject}
                        messageCategory={message.messageCategory}
                        showParticipantsNotice={
                          message.isReplyVisibleToOtherParticipants ?? false
                        }
                        onCancel={handleCancel}
                        onSuccess={() => {
                          queryClient.invalidateQueries({
                            queryKey: [
                              'resident-message-timeline',
                              contractUuid,
                            ],
                          })
                          queryClient.invalidateQueries({
                            queryKey: residentMessageThreadKey(
                              contractUuid,
                              messageThreadUuid
                            ),
                          })
                        }}
                      />
                    ) : message.canReply !== false ? (
                      <div className="flex justify-end mb-5">
                        <Button
                          onClick={() => {
                            setFormState({
                              messageThreadUuid,
                              replySubject: message.entries[0].title,
                              focusSubject: true,
                            })
                            setShowForm(true)
                          }}
                        >
                          {t('messages:view.reply')}
                        </Button>
                      </div>
                    ) : null}
                  </>
                )}

                <div
                  className={
                    showForm ? 'border-t border-common-gray-4 pt-6' : ''
                  }
                >
                  <MessageCardTimeline>
                    <ThreadStack
                      open={true}
                      thread={message}
                      onViewThread={null}
                      header={
                        message.messageCategory !== undefined ? (
                          <MessageCategories
                            value={
                              message.messageCategory
                                ? String(message.messageCategory.id)
                                : ''
                            }
                            categories={getReadOnlyMessageCategoryOptions(
                              message.messageCategory,
                              [],
                              i18n.language,
                              t('messages:form.noCategory')
                            )}
                            disabled
                          />
                        ) : undefined
                      }
                    />
                  </MessageCardTimeline>
                </div>
              </>
            </View.Content>
          </ThreadLayout>
        )}
      </MessageThreadViewDataLoader>
    </Suspense>
  ) : (
    <NotFound />
  )
}

const ThreadLayout = ({
  children,
  title,
}: {
  children: React.ReactNode
  title: string | null
}): React.ReactElement => {
  const { messagesPath } = useContractPaths()
  const { t } = useTranslation(['common', 'messages', 'titles'])

  return (
    <TitleController title={title ?? t('titles:messages')}>
      <View>
        <View.Header>
          <View.HeaderLink to={messagesPath}>
            {t('messages:view.previous')}
          </View.HeaderLink>
          <View.HeaderTitle>{t('messages:view.messageTitle')}</View.HeaderTitle>
        </View.Header>

        <View.Sidebar>
          <SidebarInfoSection>
            <SidebarInfoSectionText>
              {t('messages:view.description')}
            </SidebarInfoSectionText>
          </SidebarInfoSection>
        </View.Sidebar>
        {children}
      </View>
    </TitleController>
  )
}
